import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { AdminUser, SubtaskAssigneeMenuComponent } from '../../../request-v2/components/shared/subtask-assignee-menu/subtask-assignee-menu.component';
import { parseDateForDisplay } from '../../../global/utils/date/date.utils';
import { AdminPermitDetail, PermitSubtask } from '../../models/permit.model';
import { PermitV2Service } from '../../services/permits.abstract.service';
import { UserService } from '../../../user/service/user.abstract.service';
import { MessageService } from '../../../global/services/message/message.abstract.service';

@Component({
    selector: 'app-permit-subtasks',
    templateUrl: './permit-subtasks.component.html',
    styleUrls: ['./permit-subtasks.component.scss'],
})
export class PermitSubtasksComponent implements OnInit, OnChanges, OnDestroy {
    @Input() permit: AdminPermitDetail;
    @Output() permitUpdated = new EventEmitter<void>();
    @ViewChild('assigneeMenu') assigneeMenu: SubtaskAssigneeMenuComponent;
    @ViewChild('newTaskInput') newTaskInput: ElementRef;
    @ViewChild('sharedDuePicker') sharedDuePicker: MatDatepicker<any>;

    subtasks: PermitSubtask[] = [];
    newDescription = '';
    newDueDate = '';
    selectedAssignee: AdminUser | null = null;
    isAdding = false;
    isCreating = false;
    confirmingDeleteId: string | null = null;
    editingTaskId: string | null = null;
    editingTaskText = '';
    editingDueDateSubtaskId: string | null = null;
    activeSubtaskId: string | null = null;
    assigningSubtaskId: string | null = null;
    preloadedAdminData: AdminUser[] = [];

    private destroy$ = new Subject<void>();

    constructor(
        private permitService: PermitV2Service,
        private userService: UserService,
        private translate: TranslateService,
        private messageService: MessageService,
        private elementRef: ElementRef,
    ) {}

    ngOnInit(): void {
        this.syncSubtasks();
        this.loadAdminUsers();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.permit && !changes.permit.firstChange) this.syncSubtasks();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @HostListener('document:mousedown', ['$event'])
    onDocumentMousedown(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (target?.closest?.('.cdk-overlay-container')) return;

        if (!this.elementRef.nativeElement.contains(event.target as Node)) {
            this.confirmingDeleteId = null;
            if (this.isAdding && !this.newDescription.trim()) this.dismissAdding();
        }
    }

    private syncSubtasks(): void {
        this.subtasks = this.permit?.subtasks ? [...this.permit.subtasks] : [];
    }

    isChecked(subtask: PermitSubtask): boolean {
        return !!(subtask.isDone || subtask.completed);
    }

    startTextEdit(subtask: PermitSubtask): void {
        if (this.confirmingDeleteId) return;
        this.editingTaskId = subtask._id;
        this.editingTaskText = subtask.title || '';
        setTimeout(() => {
            const input = this.elementRef.nativeElement.querySelector('.task-text-input') as HTMLInputElement;
            input?.focus();
            input?.select();
        });
    }

    saveTextEdit(subtask: PermitSubtask): void {
        if (this.editingTaskId !== subtask._id) return;
        const description = this.editingTaskText.trim();
        if (!description || description === subtask.title) {
            this.cancelTextEdit();
            return;
        }
        this.editingTaskId = null;
        this.permitService
            .updateSubtask(this.permit._id, subtask._id, { description })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    subtask.title = description;
                    this.permitUpdated.emit();
                },
                error: () => this.messageService.error(this.translate.instant('permit.manage.common.error')),
            });
    }

    cancelTextEdit(): void {
        this.editingTaskId = null;
        this.editingTaskText = '';
    }

    toggleDone(subtask: PermitSubtask): void {
        const isDone = !this.isChecked(subtask);
        this.permitService
            .updateSubtask(this.permit._id, subtask._id, { isDone })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    subtask.isDone = isDone;
                    subtask.completed = isDone;
                    this.permitUpdated.emit();
                },
                error: () => this.messageService.error(this.translate.instant('permit.manage.common.error')),
            });
    }

    startAdding(): void {
        this.isAdding = true;
        this.newDescription = '';
        this.newDueDate = '';
        this.selectedAssignee = null;
        setTimeout(() => this.newTaskInput?.nativeElement?.focus(), 50);
    }

    dismissAdding(): void {
        this.isAdding = false;
        this.newDescription = '';
        this.newDueDate = '';
        this.selectedAssignee = null;
    }

    addSubtask(): void {
        const description = this.newDescription.trim();
        if (!description || this.isCreating) return;
        this.isCreating = true;
        const item: any = { description };
        if (this.selectedAssignee) item.assignedTo = this.selectedAssignee.id;
        if (this.newDueDate) item.dueDate = this.newDueDate;
        this.permitService
            .addSubtasks(this.permit._id, { subtasks: [item] })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.isCreating = false;
                    this.dismissAdding();
                    this.permitUpdated.emit();
                },
                error: () => {
                    this.isCreating = false;
                    this.messageService.error(this.translate.instant('permit.manage.common.error'));
                },
            });
    }

    openDueDatePicker(subtaskId: string): void {
        this.editingDueDateSubtaskId = subtaskId;
        setTimeout(() => this.sharedDuePicker?.open());
    }

    onDueDateChange(event: any): void {
        const subtaskId = this.editingDueDateSubtaskId;
        this.editingDueDateSubtaskId = null;
        const raw = event.value;
        if (!raw) return;
        const date: Date = raw.toDate ? raw.toDate() : new Date(raw);
        if (isNaN(date.getTime())) return;
        const dueDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        if (subtaskId === 'new') {
            this.newDueDate = dueDate;
            return;
        }
        const subtask = this.subtasks.find((item) => item._id === subtaskId);
        if (!subtask) return;
        this.permitService
            .updateSubtask(this.permit._id, subtask._id, { dueDate })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    subtask.dueDate = dueDate;
                    this.permitUpdated.emit();
                },
                error: () => this.messageService.error(this.translate.instant('permit.manage.common.error')),
            });
    }

    clearDueDate(): void {
        const subtaskId = this.editingDueDateSubtaskId;
        this.editingDueDateSubtaskId = null;
        if (subtaskId === 'new') {
            this.newDueDate = '';
            return;
        }
        const subtask = this.subtasks.find((item) => item._id === subtaskId);
        if (!subtask) return;
        this.permitService
            .updateSubtask(this.permit._id, subtask._id, { dueDate: '' })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    subtask.dueDate = undefined;
                    this.permitUpdated.emit();
                },
                error: () => this.messageService.error(this.translate.instant('permit.manage.common.error')),
            });
    }

    formatDueDate(iso: string): string {
        const date = parseDateForDisplay(iso);
        if (!date) return '';
        const locale = this.translate.currentLang === 'fr' ? 'fr-CA' : 'en-CA';
        return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
    }

    getDueStatus(iso: string): 'past' | 'soon' | 'future' {
        const due = parseDateForDisplay(iso);
        if (!due) return 'future';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        due.setHours(0, 0, 0, 0);
        const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000);
        if (diffDays < 0) return 'past';
        if (diffDays <= 1) return 'soon';
        return 'future';
    }

    loadAdminUsers(): void {
        this.userService
            .searchAdmins('', 20)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (admins: any[]) => {
                    this.preloadedAdminData = (admins || []).map((admin) => {
                        const name = admin.name || `${admin.firstName || ''} ${admin.lastName || ''}`.trim();
                        return { id: admin.id || admin._id, name, email: admin.email || '', picture: admin.picture || '', initials: this.getInitials(name) };
                    });
                },
                error: () => (this.preloadedAdminData = []),
            });
    }

    openAssigneeMenu(event: Event, subtaskId: string): void {
        event.stopPropagation();
        this.activeSubtaskId = subtaskId;
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        const subtask = this.subtasks.find((item) => item._id === subtaskId);
        const assignedTo = subtask?.assignedTo;
        const selectedId = typeof assignedTo === 'string' ? assignedTo : assignedTo?._id || this.selectedAssignee?.id || null;
        this.assigneeMenu.open({ top: rect.bottom + window.scrollY + 5, left: rect.left + window.scrollX - 230 }, selectedId, this.preloadedAdminData);
    }

    onAssigneeSelected(assignee: AdminUser | null): void {
        if (this.activeSubtaskId === 'new') {
            this.selectedAssignee = assignee;
            return;
        }
        if (this.activeSubtaskId) this.assignSubtask(this.activeSubtaskId, assignee?.id || null);
    }

    assignSubtask(subtaskId: string, userId: string | null): void {
        this.assigningSubtaskId = subtaskId;
        this.permitService
            .assignSubtask(this.permit._id, subtaskId, { assignedTo: userId })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.assigningSubtaskId = null;
                    this.activeSubtaskId = null;
                    this.permitUpdated.emit();
                },
                error: () => {
                    this.assigningSubtaskId = null;
                    this.messageService.error(this.translate.instant('permit.manage.common.error'));
                },
            });
    }

    getAssigneePicture(assignedTo: string | { _id?: string; picture?: string }): string {
        if (!assignedTo) return '';
        const assigneeId = typeof assignedTo === 'string' ? assignedTo : assignedTo._id;
        return (typeof assignedTo !== 'string' && assignedTo.picture) || this.preloadedAdminData.find((admin) => admin.id === assigneeId)?.picture || '';
    }

    getAssigneeInitials(assignedTo: string | { firstName?: string; lastName?: string }): string {
        if (!assignedTo) return '';
        if (typeof assignedTo === 'string') {
            return this.preloadedAdminData.find((admin) => admin.id === assignedTo)?.initials || '';
        }
        return `${assignedTo.firstName?.charAt(0) || ''}${assignedTo.lastName?.charAt(0) || ''}`.toUpperCase();
    }

    getAssigneeName(assignedTo: string | { firstName?: string; lastName?: string }): string {
        if (!assignedTo) return '';
        if (typeof assignedTo === 'string') {
            return this.preloadedAdminData.find((admin) => admin.id === assignedTo)?.name || '';
        }
        return `${assignedTo.firstName} ${assignedTo.lastName}`.trim();
    }

    executeDelete(subtaskId: string): void {
        this.permitService
            .deleteSubtask(this.permit._id, subtaskId)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.subtasks = this.subtasks.filter((item) => item._id !== subtaskId);
                    this.confirmingDeleteId = null;
                    this.permitUpdated.emit();
                },
                error: () => this.messageService.error(this.translate.instant('permit.manage.common.error')),
            });
    }

    getInitials(name: string): string {
        return (name || '')
            .split(' ')
            .map((part) => part[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    }
}
